import {Component, ConfigOptions} from 'panel';

import {registerMPElement} from '../../../util/register-element';
import {formatPhoneNumber, parsePhoneNumber} from '../../../util/string';
import {ENTER_KEY_CODE, ESCAPE_KEY_CODE} from '../../../util/dom';
import {SettingsLiState, SettingsLiAttrs} from './types';
import template from './index.jade';
import css from './index.styl?inline';

const INPUT_TYPES = {
  TEXT: `text`,
  PHONE_NUMBER: `phone-number`,
};

export class SettingsLi extends Component<SettingsLiState, {}, unknown, SettingsLiAttrs> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      // This component has way too many attributes, some of which are conflicting and is need of a refactor
      'all-tags': `json`,
      button: `string`,
      buttontext: `string`,
      'custom-handling': `boolean`,
      'delete-button': `boolean`,
      disabled: `boolean`,
      editable: `boolean`,
      editing: `boolean`,
      emptiable: `boolean`,
      fileaccept: {type: `string`, default: `*`},
      'hide-buttons': `boolean`,
      isfile: {type: `boolean`, description: `DEPRECATED: use 'type'`},
      isswitch: {type: `boolean`, description: `DEPRECATED: use 'type'`},
      label: `string`,
      placeholder: `string`,
      'read-only-tags': `json`,
      removable: `boolean`,
      'selected-tags': `json`,
      info: `string`,
      switchon: `boolean`,
      type: {
        type: `string`,
        default: ``,
        enum: [`text`, `password`, `tag`, `select`, `file`, `phone-number`, `slot`],
      },
      value: `string`,
    };
  }

  get config(): ConfigOptions<SettingsLiState, {}, SettingsLiAttrs> {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        editing: false,
        password1: ``,
        password2: ``,
        countryCode: ``,
        localPhoneNumber: ``,
        // TODO - add a type attribute that governs these behaviors
        type: `text`,
        isFile: false,
        isSwitch: false,
        switchOn: false,
        allTags: new Set(),
        readonlyTags: new Set(),
        selectedTags: new Set(),
        originalValue: null,
        originalSelectedTags: new Set(),
        inputValue: ``,
        saving: false,
        selectItems: [],
        selectMenuOpen: false,
        selectTagOpen: false,
        selectedValue: {},
        uploading: false,
        saveDisabled: false,
        removable: false,
        validationError: null,
      },

      helpers: {
        tagSelectorChanged: (ev) => {
          const tagName = ev.detail.tagName;
          const selectedTags = this.state.selectedTags;
          const allTags = this.state.allTags;
          if (ev.detail.action === `addTag`) {
            this.update({
              selectedTags: (selectedTags.add(tagName), selectedTags),
              allTags: (allTags.add(tagName), allTags),
            });
          } else if (ev.detail.action === `removeTag`) {
            this.update({selectedTags: (selectedTags.delete(tagName), selectedTags)});
          }
        },
        switchChanged: (ev) => {
          this.update({switchOn: ev.detail.on});
          this.dispatchEvent(new CustomEvent(`switchChange`, {detail: ev.detail}));
        },
        handleKeydown: (ev) => {
          const saveHandler = this.attr(`custom-handling`) ? this.helpers.customSave : this.helpers.clickedSave;
          const cancelHandler = this.attr(`custom-handling`) ? this.helpers.customCancel : this.helpers.stopEditing;
          if (ev.keyCode === ENTER_KEY_CODE) {
            saveHandler(ev);
            ev.preventDefault();
          } else if (ev.keyCode === ESCAPE_KEY_CODE) {
            cancelHandler(ev);
          }
        },
        clickedButton: () => this.dispatchEvent(new CustomEvent(`button-click`)),
        clickedSave: async () => {
          let value: any = this.state.inputValue;
          if (this.isSelect) {
            value = this.state.selectedValue;
          } else if (this.attr(`type`) === `password`) {
            value = this.state.password1;
          } else if (this.attr(`type`) === `tag`) {
            value = this.state.selectedTags;
          } else if (this.state.type === INPUT_TYPES.PHONE_NUMBER) {
            value = {countryCode: this.state.countryCode, localPhoneNumber: this.state.localPhoneNumber};
          }

          if (this.validator) {
            try {
              const res = this.validator(value);
              if (res.error) {
                this.update({validationError: res.error});
                return;
              } else {
                this.update({validationError: ``});
              }
            } catch (err) {
              this.update({validationError: err.message});
              return;
            }
          }

          this.update({saving: true});
          const succeeded = await this.updateFunc(value);
          if (succeeded) {
            this.update({editing: false});
          }
          this.updateInput();
          this.update({saving: false});
        },
        clickedUpload: () => {
          this.fileEl.click();
        },
        clickedRemove: (ev) => {
          this.dispatchEvent(new CustomEvent(`removeClick`, {detail: ev.detail}));
        },
        inputType: () =>
          this.attr(`type`) ||
          (this.state.isFile ? `file` : this.state.isSwitch ? `switch` : this.isSelect ? `select` : `text`),
        label: () => this.attr(`label`),
        startEditing: async () => {
          if (this.preEdit && !(await this.preEdit())) {
            return;
          }

          let updateProps: Partial<SettingsLiState> = {
            editing: true,
            selectMenuOpen: true,
            selectTagOpen: true,
            validationError: null,
            password1: ``,
            password2: ``,
          };
          if (this.state.type === INPUT_TYPES.PHONE_NUMBER) {
            try {
              const {countryCode, localPhoneNumber} = parsePhoneNumber(this.state.inputValue);
              updateProps = {...updateProps, countryCode, localPhoneNumber, originalValue: this.state.inputValue};
            } catch (err) {
              this.update({...updateProps, validationError: err.message});
            }
          }

          this.update(updateProps);

          requestAnimationFrame(() => this.inputEl.focus());
          this.dispatchEvent(new CustomEvent(`editClicked`));
        },
        stopEditing: () => {
          this.updateInput();

          const updateProps: Partial<SettingsLiState> = {
            editing: false,
            selectMenuOpen: false,
            selectTagOpen: false,
            validationError: null,
            password1: ``,
            password2: ``,
            countryCode: ``,
            localPhoneNumber: ``,
          };
          if (this.state.type === INPUT_TYPES.PHONE_NUMBER) {
            updateProps.inputValue = this.state.originalValue;
            updateProps.originalValue = ``;
          }

          this.update(updateProps);
        },
        stopUploading: () => {
          this.update({uploading: false});
        },
        handleTagMenuChange: (ev) => {
          if (ev.detail.state === `closed`) {
            this.update({
              selectTagOpen: false,
              selectedTags: this.state.originalSelectedTags,
              originalSelectedTags: new Set(),
            });
            this.helpers.stopEditing();
          } else if (ev.detail.state === `open`) {
            this.state.originalSelectedTags = new Set(this.state.selectedTags);
          }
        },
        handleSelectMenuChange: (ev) => {
          if (ev.detail.state === `closed`) {
            this.update({selectMenuOpen: false});
          }
        },
        selectItem: (ev) => this.update({selectedValue: ev.detail.item, selectMenuOpen: false}),
        toggleSelectMenu: () => this.update({selectMenuOpen: !this.state.selectMenuOpen}),

        getTagOptions: () =>
          Array.from(
            new Set([
              ...Array.from(this.state.allTags),
              ...Array.from(this.state.readonlyTags),
              ...Array.from(this.state.selectedTags),
            ]),
          ).map((label) => ({
            label,
            value: label,
            isSelected: this.state.selectedTags.has(label),
            isDisabled: this.state.readonlyTags.has(label),
          })),

        fileInserted: (vnode) => {
          this.fileEl = vnode.elm;
        },
        fileDialogEnded: (ev) => {
          this.update({saving: true});
          this.dispatchEvent(new CustomEvent(`fileAdded`, {detail: ev.target}));
        },
        inputInserted: (vnode) => (this.inputEl = vnode.elm),
        inputUpdated: (ev) => {
          this.update({inputValue: ev.target.value, validationError: null});
          this.dispatchEvent(new CustomEvent(`inputUpdate`, {detail: ev.detail}));
        },
        passwordUpdated: (num, ev) => this.update({[`password${num}`]: ev.target.value}),
        phoneNumberUpdated: (ev) => this.phoneNumberUpdated(ev),
        uploadFile: () => {
          this.update({uploading: true});
        },
        customSave: (ev) => {
          this.dispatchEvent(new CustomEvent(`saveClick`, {detail: ev.detail}));
        },
        customCancel: (ev) => {
          this.dispatchEvent(new CustomEvent(`cancelClick`, {detail: ev.detail}));
        },
        parsePhoneNumber: (phoneNumber) => parsePhoneNumber(phoneNumber),
      },
    };
  }

  fileEl: HTMLInputElement;
  inputEl: HTMLInputElement;

  isSelect: boolean;
  preEdit: () => Promise<boolean>;
  validator: (value: any) => any;
  updateFunc: (value: any) => Promise<boolean>;

  private _rawSelectItems: any[];

  connectedCallback() {
    super.connectedCallback();
    this.updateInput();
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `value`) {
      this.updateInput();
    } else if (attr === `isswitch`) {
      this.update({isSwitch: this.attr(`isswitch`)});
    } else if (attr === `switchon`) {
      this.update({switchOn: this.attr(`switchon`)});
    } else if (attr === `isfile`) {
      this.update({isFile: this.attr(`isfile`)});
    } else if (attr === `editing`) {
      this.update({editing: this.attr(`editing`)});
    } else if (attr === `removable`) {
      this.update({removable: this.attr(`removable`)});
    } else if (attr === `all-tags`) {
      this.update({allTags: new Set(this.attr(`all-tags`))});
    } else if (attr === `selected-tags`) {
      this.update({selectedTags: new Set(this.attr(`selected-tags`))});
    } else if (attr === `read-only-tags`) {
      this.update({readonlyTags: new Set(this.attr(`read-only-tags`))});
    } else if (attr === `type`) {
      this.update({type: this.attr(`type`)});
    }
  }

  updateInput() {
    const value = this.attr(`value`);
    if (this.isSelect) {
      const idValue = parseInt(value, 10);
      this.update({
        selectedValue: this._rawSelectItems.find((item) => item.id === idValue),
        selectItems: this._rawSelectItems.map((item) => Object.assign({isSelected: item.id === idValue}, item)),
      });
    } else if (this.state.isFile) {
      this.update({inputValue: value, removable: !!value});
    } else if (this.state.type === INPUT_TYPES.PHONE_NUMBER && this.state.editing) {
      const phoneNumber = formatPhoneNumber(this.state.countryCode, this.state.localPhoneNumber, false);
      if (phoneNumber) {
        this.update({inputValue: phoneNumber});
      }
    } else {
      this.update({inputValue: value});
    }
  }

  phoneNumberUpdated(ev) {
    if (ev.target.name === `country-code`) {
      this.update({countryCode: ev.target.value});
    } else if (ev.target.name === `local-phone-number`) {
      this.update({localPhoneNumber: ev.target.value});
    }
  }

  set selectItems(val) {
    this.isSelect = true;
    this._rawSelectItems = val;
    this.updateInput();
  }
}

export default registerMPElement(`mp-settings-li`, SettingsLi);
